import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import axios from "axios";
import { Table, Row, Col, Form } from "react-bootstrap";
import PageLayout from "../../../../layouts/PageLayout";
import { Link } from "react-router-dom";

export default function ProductFinanceReport() {
    const { t } = useContext(TranslatorContext);

    const API_BASE = `${process.env.REACT_APP_API_URL}/admin/finance/product`;
    const INITIAL_DATA_API = `${process.env.REACT_APP_API_URL}/admin/finance/initial-data`;
    const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } };

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [stores, setStores] = useState([]);
    const [brands, setBrands] = useState([]);
    const [listers, setListers] = useState([]);
    const [marketPlaces, setMarketPlaces] = useState([]);

    const [filters, setFilters] = useState({
        search: "",
        lister_id: "",
        store_id: "",
        brand_id: "",
        marketplace_id: ""
    });

    const [totals, setTotals] = useState({
        totalRevenue: 0,
        totalExpenses: 0,
        totalProfit: 0
    });

    const fetchInitialData = useCallback(async () => {
        try {
            const res = await axios.get(INITIAL_DATA_API, authHeaders);
            const data = res.data?.data || {};
            setStores(data.stores || []);
            setBrands(data.brands || []);
            setListers(data.listers || []);
            setMarketPlaces(data.market_places || []);
        } catch (err) {
            console.error("Error fetching initial data:", err);
        }
    }, [INITIAL_DATA_API]);

    const fetchFinanceData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.search) params.search = filters.search;
            if (filters.lister_id) params.lister_id = filters.lister_id;
            if (filters.store_id) params.store_id = filters.store_id;
            if (filters.brand_id) params.brand_id = filters.brand_id;
            if (filters.marketplace_id) params.marketplace_id = filters.marketplace_id;

            const res = await axios.get(API_BASE, { ...authHeaders, params });
            const list = res.data?.data || [];
            setProducts(list);

            let revenue = 0;
            let expense = 0;
            let profit = 0;

            list.forEach((item) => {
                revenue += parseFloat(item.amounts?.order_price || 0);
                expense += parseFloat(item.amounts?.total_expense || 0);
                profit += parseFloat(item.amounts?.profit || 0);
            });

            setTotals({
                totalRevenue: revenue,
                totalExpenses: expense,
                totalProfit: profit
            });
        } catch (err) {
            console.error("Error fetching finance data:", err);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, filters]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchFinanceData();
        }, 300);
        return () => clearTimeout(timer);
    }, [fetchFinanceData]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleResetFilters = () => {
        setFilters({
            search: "",
            lister_id: "",
            store_id: "",
            brand_id: "",
            marketplace_id: ""
        });
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t("Product Finance Report")}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t("finance report")}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={4} md={6} className="mb-4">
                    <div className="mc-card p-4 d-flex align-items-center justify-content-between" style={{ borderLeft: "5px solid #28a745", background: "#f8f9fa" }}>
                        <div>
                            <span className="text-muted fw-bold text-uppercase d-block mb-1" style={{ fontSize: "12px" }}>
                                {t("Total Revenue (In)")}
                            </span>
                            <h3 className="mb-0 text-success fw-bold">
                                ${totals.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </h3>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(40, 167, 69, 0.1)" }}>
                            <i className="material-icons text-success" style={{ fontSize: "28px" }}>arrow_downward</i>
                        </div>
                    </div>
                </Col>

                <Col xl={4} md={6} className="mb-4">
                    <div className="mc-card p-4 d-flex align-items-center justify-content-between" style={{ borderLeft: "5px solid #dc3545", background: "#f8f9fa" }}>
                        <div>
                            <span className="text-muted fw-bold text-uppercase d-block mb-1" style={{ fontSize: "12px" }}>
                                {t("Total Expenses (Out)")}
                            </span>
                            <h3 className="mb-0 text-danger fw-bold">
                                ${totals.totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </h3>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(220, 53, 69, 0.1)" }}>
                            <i className="material-icons text-danger" style={{ fontSize: "28px" }}>arrow_upward</i>
                        </div>
                    </div>
                </Col>

                <Col xl={4} md={12} className="mb-4">
                    <div className="mc-card p-4 d-flex align-items-center justify-content-between" style={{ borderLeft: "5px solid #4f46e5", background: "#f8f9fa" }}>
                        <div>
                            <span className="text-muted fw-bold text-uppercase d-block mb-1" style={{ fontSize: "12px" }}>
                                {t("Net Profit")}
                            </span>
                            <h3 className={`mb-0 fw-bold ${totals.totalProfit >= 0 ? "text-primary" : "text-danger"}`}>
                                ${totals.totalProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </h3>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(79, 70, 229, 0.1)" }}>
                            <i className="material-icons style-primary" style={{ fontSize: "28px", color: "#4f46e5" }}>account_balance_wallet</i>
                        </div>
                    </div>
                </Col>

                <Col xl={12} className="mb-4">
                    <div className="mc-card p-3">
                        <div className="mc-card-header mb-3">
                            <h5 className="mc-card-title mb-0">{t("Filter Reports")}</h5>
                        </div>
                        <Row className="g-3">
                            <Col lg={3} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t("Search")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="search"
                                        placeholder={t("Title or Code...")}
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col lg={2} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t("Store")}</Form.Label>
                                    <Form.Select name="store_id" value={filters.store_id} onChange={handleFilterChange}>
                                        <option value="">{t("All Stores")}</option>
                                        {stores.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col lg={2} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t("Brand")}</Form.Label>
                                    <Form.Select name="brand_id" value={filters.brand_id} onChange={handleFilterChange}>
                                        <option value="">{t("All Brands")}</option>
                                        {brands.map((b) => (
                                            <option key={b.id} value={b.id}>{b.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col lg={2} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t("Lister")}</Form.Label>
                                    <Form.Select name="lister_id" value={filters.lister_id} onChange={handleFilterChange}>
                                        <option value="">{t("All Listers")}</option>
                                        {listers.map((l) => (
                                            <option key={l.id} value={l.id}>{l.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col lg={2} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t("Marketplace")}</Form.Label>
                                    <Form.Select name="marketplace_id" value={filters.marketplace_id} onChange={handleFilterChange}>
                                        <option value="">{t("All Marketplaces")}</option>
                                        {marketPlaces.map((m) => (
                                            <option key={m.id} value={m.id}>{m.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col lg={1} md={6} className="d-flex align-items-end">
                                <button type="button" className="btn btn-secondary w-100" onClick={handleResetFilters}>
                                    {t("Reset")}
                                </button>
                            </Col>
                        </Row>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header p-3">
                            <h5 className="mc-card-title mb-0">{t("Product Finance Breakdown")}</h5>
                        </div>
                        <div className="table-responsive-wrapper">
                            <Table responsive className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: "0" }}>
                                <thead style={{ backgroundColor: "#4f46e5", color: "#ffffff" }}>
                                    <tr>
                                        <th className="text-white text-center" style={{ width: "60px" }}>#</th>
                                        <th className="text-white">{t("PRODUCT DETAILS")}</th>
                                        <th className="text-white">{t("EXPENSE BREAKDOWN")}</th>
                                        <th className="text-white text-center" style={{ width: "260px" }}>{t("FINANCIAL SUMMARY")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="4" className="text-center p-4">{t("Loading finance data...")}</td>
                                        </tr>
                                    ) : products.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="text-center p-4">{t("No financial records found.")}</td>
                                        </tr>
                                    ) : (
                                        products.map((item, index) => (
                                            <tr key={item.id || index} style={{ borderBottom: "1px solid #e5e7eb" }}>
                                                <td className="text-center fw-bold">{index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        {item.card_img ? (
                                                            <img
                                                                src={`${process.env.REACT_APP_IMG_URL}/${item.card_img}`}
                                                                alt={item.title}
                                                                className="rounded"
                                                                style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                                            />
                                                        ) : (
                                                            <div className="rounded bg-light d-flex align-items-center justify-content-center text-muted" style={{ width: "50px", height: "50px", fontSize: "10px" }}>
                                                                NO IMG
                                                            </div>
                                                        )}
                                                        <div>
                                                            <h6 className="mb-1 text-dark fw-bold" style={{ fontSize: "14px" }}>{item.title}</h6>
                                                            <div className="d-flex flex-wrap gap-2 text-muted" style={{ fontSize: "12px" }}>
                                                                <span><strong>Code:</strong> {item.product_code}</span>
                                                                <span>|</span>
                                                                <span><strong>Store:</strong> {item.store?.name || "N/A"}</span>
                                                                <span>|</span>
                                                                <span><strong>Brand:</strong> {item.brand?.name || "N/A"}</span>
                                                                <span>|</span>
                                                                <span><strong>Marketplace:</strong> {item.marketplace?.name || "N/A"}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ verticalAlign: "middle" }}>
                                                    <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                                                        {item.expense_breakdown?.map((exp, expIdx) => (
                                                            <div key={exp.key || expIdx} className="d-flex justify-content-between pe-4">
                                                                <span className="text-muted">
                                                                    {exp.name} {exp.percentage ? `(${exp.percentage}%)` : ""}:
                                                                </span>
                                                                <span className="fw-bold text-dark">${exp.amount || "0.00"}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="align-middle px-3" style={{ verticalAlign: "middle" }}>
                                                    <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span className="text-muted fw-bold">Order Price:</span>
                                                            <span className="fw-bold text-dark">${item.amounts?.order_price || "0.00"}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="text-muted fw-bold">Total Expense:</span>
                                                            <span className="fw-bold text-danger">${item.amounts?.total_expense || "0.00"}</span>
                                                        </div>
                                                        <div
                                                            className="rounded p-2 text-center text-white fw-bold shadow-sm"
                                                            style={{
                                                                backgroundColor: parseFloat(item.amounts?.profit || 0) >= 0 ? "#4f46e5" : "#dc3545",
                                                                fontSize: "15px"
                                                            }}
                                                        >
                                                            PROFIT: ${item.amounts?.profit || "0.00"}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}